// 214 addendum 4 §25 [RULED 63 — Laurent, 04/10] — AN IMPORT ASKS BEFORE IT LOSES ANYTHING. Before it writes, an
// import works out what it would lose — a value emptied, an entry taken out of a list, a row archived or deleted, a
// member unlinked — matter by matter and client by client, and waits for an OK; when nothing would be lost it imports
// at once. This file is the PURE half every importer shares: the plan's shape, its totals, the row diff that finds
// the losses, and the « Importer sans vider » variant of a row's data (an empty cell leaves the stored value, what a
// cell states still applies). The window that shows a plan is src/components/ImportLossDialog.tsx.
//
// Laurent's final glance of 04/10 is what this is for: his partial sheets (every heading kept, every cell emptied but
// Ref, Title and Status) changed two statuses and, with them — under §24's rule, an empty cell under a heading the
// sheet has clears — emptied 53 values on 11 matters and unlinked 305 members, with no word said first
// (docs/214-deploy-note.md §7.27). Every reader here takes `unknown` where a view's row is untyped, so an importer
// adds no unsafe read of its own (the ESLint ratchet); the callback shapes are `typeof` exported value witnesses
// whose bodies read their parameters (214 [METHOD] (c) — a function type literal's parameter names count as unused).

/** What a write would do to a stored value. `deleted` is the clients list's and the time entries' (the engine's
 *  delete is a DELETE there, an archive on the matter lists) [ADVISOR — a fifth kind beside the ruling's four]. */
import type { NameOf } from "./fnTypes";

export type LossKind = "emptied" | "removed" | "archived" | "deleted" | "unlinked";
export const LOSS_KINDS: readonly LossKind[] = ["emptied", "removed", "archived", "deleted", "unlinked"];

/** One loss: the field it is on (the sheet's own header where there is one) and the value held today, as text. */
export interface LossLine {
  kind: LossKind;
  field: string;
  value: string;
}
/** One matter (its ref) or one client (his name or id) and what it would lose. */
export interface LossGroup {
  key: string;
  label: string;
  lines: LossLine[];
}
export interface LossPlan {
  groups: LossGroup[];
}

export const newLossPlan = (): LossPlan => ({ groups: [] });

/** Adds `line` under the group `key` (made with `label` when first seen). Returns the plan for chaining. */
export function addLoss(plan: LossPlan, key: string, label: string, line: LossLine): LossPlan {
  let g = plan.groups.find((x) => x.key === key);
  if (!g) {
    g = { key, label, lines: [] };
    plan.groups.push(g);
  }
  g.lines.push(line);
  return plan;
}

export const hasLoss = (plan: LossPlan): boolean => plan.groups.some((g) => g.lines.length > 0);
export const hasEmptied = (plan: LossPlan): boolean => plan.groups.some((g) => g.lines.some((l) => l.kind === "emptied"));
/** 214 addendum 5 §28 [ADVISOR] — a plan that TAKES A ROW AWAY: a matter archived, a client or a time entry deleted, a
 *  member unlinked (a Replace run's roster PUT deletes his link with his payments and stated dues — the census's own
 *  words, docs/214-deploy-note.md §7.25 item 3 (a)). The window then draws « Tout importer » as the app's destructive
 *  button; a plan that only empties values or takes entries out of a list keeps the import's own. */
export const takesRowsAway = (plan: LossPlan): boolean =>
  plan.groups.some((g) => g.lines.some((l) => l.kind === "archived" || l.kind === "deleted" || l.kind === "unlinked"));
export const countOf = (plan: LossPlan, kind: LossKind): number =>
  plan.groups.reduce((n, g) => n + g.lines.filter((l) => l.kind === kind).length, 0);
/** The kinds a plan carries, in the ruling's order, each with its count. */
export const totalsOf = (plan: LossPlan): { kind: LossKind; count: number }[] =>
  LOSS_KINDS.map((kind) => ({ kind, count: countOf(plan, kind) })).filter((x) => x.count > 0);

/** An empty value, as a sheet cell produces one: nothing, "", [], {}. A 0 is a value. */
export function isEmptyValue(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") return Object.keys(v).length === 0;
  return false;
}

const asRecord = (x: unknown): Record<string, unknown> => (typeof x === "object" && x !== null ? (x as Record<string, unknown>) : {});
const asRows = (x: unknown): unknown[] => (Array.isArray(x) ? (x as unknown[]) : []);

/** The id of a list entry: a number, or an element carrying `id` (a party) or `clId` (a member). */
export function entryId(x: unknown): number | null {
  if (typeof x === "number") return Number.isFinite(x) ? x : null;
  if (typeof x === "string") return x.trim() !== "" && Number.isFinite(Number(x)) ? Number(x) : null;
  if (typeof x === "object" && x !== null) {
    const o = x as { id?: unknown; clId?: unknown };
    const n = Number(o.clId ?? o.id);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
const idOfRow = (row: unknown): number | null => entryId(asRecord(row).id);

const ISO_DATE_TIME = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}/;
/** A stored value as the window prints it: a date-time as its date (a stored `openDate` is an ISO timestamp). */
export function textOfValue(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") {
    const m = ISO_DATE_TIME.exec(v);
    return m ? m[1] : v;
  }
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return (v as unknown[]).map((x) => textOfValue(x)).join(", ");
  if (typeof v === "object") {
    // an element of a list: its id (a party), its clId (a member)
    const id = entryId(v);
    return id === null ? JSON.stringify(v) : `#${String(id)}`;
  }
  return "";
}

// ── the callback shapes, as exported value witnesses whose bodies read their parameters (src/lib/fnTypes.ts) ───
export const namerWitness = (field: string, id: number): string | null => (field.length > 0 && id > 0 ? field : null);
/** A name for an entry of a list column, when the caller can give one (a pool's row), else null → `#id`. */
export type EntryNamer = typeof namerWitness;
/** A text read off a row the caller knows: its label, its value, its field — `pname`'s shape (fnTypes' NameOf). */
export type RowText = NameOf;
export const idTextWitness = (clId: number): string => String(clId);
/** A member's name for his id. */
export type IdText = typeof idTextWitness;

/** The columns a row diff reads: the data key and the sheet's header for it (what the window names). */
export type DiffField = readonly [key: string, header: string];

/** The entries of a stored list that the new cell does not name, one line each (by id; a name where the caller has one). */
function removedEntries(before: readonly unknown[], after: unknown, key: string, header: string, names: EntryNamer | undefined): LossLine[] {
  const kept = new Set(asRows(after).map(entryId).filter((n): n is number => n !== null));
  const out: LossLine[] = [];
  for (const x of before) {
    const id = entryId(x);
    if (id === null || kept.has(id)) continue;
    const nm = names ? names(key, id) : null;
    out.push({ kind: "removed", field: header, value: nm ?? `#${String(id)}` });
  }
  return out;
}

/** THE ROW DIFF: what writing `data` over `stored` would lose, field by field — a scalar emptied, an entry taken
 *  out of a list (by id). Only the keys `data` carries are read: a key the sheet does not have is not written
 *  (§24) and is not a loss. A list cell that names fewer ids is a loss for each id it drops; one that names more
 *  loses nothing. */
export function rowLosses(stored: unknown, data: Record<string, unknown>, fields: readonly DiffField[], names?: EntryNamer): LossLine[] {
  const out: LossLine[] = [];
  const row = asRecord(stored);
  for (const [key, header] of fields) {
    if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
    const before = row[key];
    const after = data[key];
    if (Array.isArray(before)) {
      out.push(...removedEntries(before as unknown[], after, key, header, names));
      continue;
    }
    if (!isEmptyValue(before) && isEmptyValue(after)) out.push({ kind: "emptied", field: header, value: textOfValue(before) });
  }
  return out;
}

/** « IMPORTER SANS VIDER » [ADVISOR]: `data` without the keys whose new value is empty while the stored one is not —
 *  an empty cell leaves the value as it is; everything a cell states (a value, a shorter list) still applies. */
export function withoutEmptying<T extends Record<string, unknown>>(data: T, stored: unknown): Partial<T> {
  const row = asRecord(stored);
  const out: Partial<T> = {};
  for (const key of Object.keys(data) as (keyof T & string)[]) {
    const after = data[key];
    if (isEmptyValue(after) && !isEmptyValue(row[key])) continue;
    out[key] = after;
  }
  return out;
}

/** The same for a row the import will MERGE into a record it has not seen yet (the clients sheet: the plan decides
 *  which record a row lands on): `data` without its empty values, the keys in `keep` kept whatever they hold (a
 *  `quantum: null` means « not stated » to the plan and must stay). */
export function withoutEmptyCells<T extends Record<string, unknown>>(data: T, keep: readonly string[] = []): Partial<T> {
  const out: Partial<T> = {};
  for (const key of Object.keys(data) as (keyof T & string)[]) {
    const v = data[key];
    if (!keep.includes(key) && (v == null || (typeof v === "string" && v.trim() === ""))) continue;
    out[key] = v;
  }
  return out;
}

/** RECORDS THE PLAN REWRITES: for every row of `after` whose id names a row of `before`, the losses of writing the
 *  one over the other (the fields given), grouped under the record's label. */
export function addRecordLosses(plan: LossPlan, before: unknown, after: unknown, fields: readonly DiffField[], labelOf: RowText): LossPlan {
  const was = new Map<number, unknown>();
  for (const r of asRows(before)) {
    const id = idOfRow(r);
    if (id !== null) was.set(id, r);
  }
  for (const r of asRows(after)) {
    const id = idOfRow(r);
    if (id === null || !was.has(id)) continue;
    const stored = was.get(id);
    for (const line of rowLosses(stored, asRecord(r), fields)) addLoss(plan, `#${String(id)}`, labelOf(stored), line);
  }
  return plan;
}

/** ROWS A REPLACE RUN TAKES AWAY: every row of `rows` — archived (the matter lists) or deleted (the clients list) —
 *  one group per row, labelled by the caller, the line's value the caller's too. */
export function addRowsTaken(plan: LossPlan, rows: unknown, kind: "archived" | "deleted", labelOf: RowText, valueOf: RowText, field = ""): LossPlan {
  for (const r of asRows(rows)) {
    const id = idOfRow(r);
    const label = labelOf(r);
    addLoss(plan, id !== null ? `#${String(id)}` : label, label, { kind, field, value: valueOf(r) });
  }
  return plan;
}

/** ROWS TAKEN under ONE group (the time entries of one matter): the lines under the group's label. */
export function addRowsTakenUnder(plan: LossPlan, groupKey: string, groupLabel: string, rows: unknown, kind: "archived" | "deleted", fieldOf: RowText, valueOf: RowText): LossPlan {
  for (const r of asRows(rows)) addLoss(plan, groupKey, groupLabel, { kind, field: fieldOf(r), value: valueOf(r) });
  return plan;
}

/** MEMBERS A REPLACE RUN UNLINKS: every link of `links` whose `clId` is not in `kept`, with what the link holds
 *  (the caller's words — his payments and stated dues), one group per member. */
export function addUnlinkedMembers(plan: LossPlan, links: unknown, kept: ReadonlySet<number>, labelOf: IdText, detailOf: RowText): LossPlan {
  for (const lk of asRows(links)) {
    const clId = entryId(asRecord(lk).clId);
    if (clId === null || kept.has(clId)) continue;
    addLoss(plan, `#${String(clId)}`, labelOf(clId), { kind: "unlinked", field: "", value: detailOf(lk) });
  }
  return plan;
}

/** The ids a plan's links name (`clId`), for `addUnlinkedMembers`. */
export const linkIds = (links: unknown): Set<number> => new Set(asRows(links).map((lk) => entryId(asRecord(lk).clId)).filter((n): n is number => n !== null));

/** A matter's label for the window: its ref, else its title, else its id. */
export function matterLabel(row: unknown): string {
  const o = asRecord(row);
  const ref = typeof o.ref === "string" ? o.ref.trim() : "";
  const title = typeof o.title === "string" ? o.title.trim() : "";
  const id = entryId(o.id);
  return ref || title || (id === null ? "" : `#${String(id)}`);
}
/** A matter's title, for the line beside its ref. */
export function matterTitle(row: unknown): string {
  const o = asRecord(row);
  return typeof o.title === "string" ? o.title : "";
}

/** The pools a matter list's window names its entries from — the view's rows under the sheet's id-column keys.
 *  Positional unknowns: a view's pools are untyped, and an argument typed `unknown` adds no unsafe read. */
export function listPools(cl: unknown, op: unknown, ocu: unknown, ex: unknown, cp: unknown, oc: unknown, me: unknown, ar: unknown, op2: unknown, co: unknown, committee?: unknown): Record<string, unknown> {
  return { clIds: cl, opIds: op, ocuIds: ocu, exIds: ex, cpIds: cp, ocIds: oc, meIds: me, arIds: ar, op2Ids: op2, responsiblePartners: co, committeeIds: committee };
}

/** A namer over the pools a view holds (`listPools`): a person's name as `pname` makes it, a company's by its
 *  name. Reads unknowns, so an untyped pool adds no unsafe read. */
export function poolNamer(pools: unknown, pname: RowText): EntryNamer {
  const bag = asRecord(pools);
  return (field, id) => {
    const pool: unknown[] = asRows(bag[field]);
    const row = pool.find((x) => idOfRow(x) === id);
    if (row === undefined) return null;
    const nm = pname(row);
    return nm && nm !== "—" ? nm : null;
  };
}
